import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { submitToHubSpot, getHubSpotCookie, getClientIP, updateHubSpotContact } from '@/lib/hubspot';
import { getLeadSourceFields } from '@/lib/lead-source';
import { linkAnonymousTracking, markFormSubmitted, getUnitInterest, updateEngagementScore } from '@/lib/unit-interest';
import { calculateScoreFromRecord, getLeadQualityFromScore } from '@/lib/lead-scoring';
import { createHubSpotDeal, getContactIdByEmail } from '@/lib/hubspot-deals';
import { getUnitByNumber, type PropertyUnit } from '@/lib/units';
import { formatCurrency } from '@/lib/utils';
import { UnitLocationMap } from '@/components/UnitLocationMap';

interface LeadFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitNumber?: string;
}

export const LeadForm = ({ open, onOpenChange, unitNumber }: LeadFormProps) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [unit, setUnit] = useState<PropertyUnit | null>(null);
  const [loadingUnit, setLoadingUnit] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    interest: [] as string[],
    message: '',
    newsletter: false,
  });

  // Fetch unit data when unitNumber changes
  useEffect(() => {
    const fetchUnit = async () => {
      if (!unitNumber) {
        setUnit(null);
        return;
      }

      setLoadingUnit(true);
      try {
        const { data, error } = await getUnitByNumber(unitNumber);
        if (error) {
          console.error('Error loading unit:', error);
          setUnit(null);
        } else {
          setUnit(data);
        }
      } catch (error) {
        console.error('Error loading unit:', error);
        setUnit(null);
      } finally {
        setLoadingUnit(false);
      }
    };

    if (open && unitNumber) {
      fetchUnit();
    } else if (!open) {
      // Reset unit when modal closes
      setUnit(null);
    }
  }, [unitNumber, open]);

  // Auto-select interest based on unit category
  useEffect(() => {
    if (!unit || formData.interest.length > 0) {
      // Don't auto-select if unit not loaded or user already selected interests
      return;
    }

    // Map unit category to interest string
    let interestToSelect: string | null = null;
    if (unit.category === '1bed') {
      interestToSelect = '1-Bedroom Residence';
    } else if (unit.category === '2bed') {
      interestToSelect = '2-Bedroom Residence';
    } else if (unit.category === 'townhouse') {
      interestToSelect = '3-Bedroom Residence';
    }

    if (interestToSelect && !formData.interest.includes(interestToSelect)) {
      setFormData(prev => ({
        ...prev,
        interest: [interestToSelect],
      }));
      
      if (import.meta.env.DEV) {
        console.log('✅ Auto-selected interest:', interestToSelect, 'for unit category:', unit.category);
      }
    }
  }, [unit, formData.interest]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate at least one interest is selected
    if (formData.interest.length === 0) {
      return;
    }
    
    setLoading(true);
    
    trackEvent('lead_started', { 
      source: 'modal', 
      interest: formData.interest.join(', '),
      ...(unitNumber ? { unit_number: unitNumber } : {})
    });
    
    // HubSpot-ready form data structure
    const hubspotCookie = getHubSpotCookie();
    const clientIP = await getClientIP();
    const leadSourceFields = getLeadSourceFields();

    if (import.meta.env.DEV) {
      console.log('📝 Name fields:', { 
        firstName: formData.firstName, 
        lastName: formData.lastName
      });
    }

    const hubspotData = {
      fields: [
        { name: 'firstname', value: formData.firstName.trim() },
        { name: 'lastname', value: formData.lastName.trim() },
        { name: 'email', value: formData.email },
        { name: 'phone', value: formData.phone },
        { name: 'interest', value: formData.interest }, // Array for multi-select
        ...(unitNumber ? [{ name: 'unit_number', value: unitNumber }] : []),
        ...(formData.message ? [{ name: 'message', value: formData.message }] : []),
        { name: 'email_marketing_opt_in', value: formData.newsletter ? 'true' : 'false' },
        // Include lead source data (UTM parameters, referrer, landing page)
        ...leadSourceFields,
      ],
      context: {
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName: typeof document !== 'undefined' ? document.title : '',
        ...(hubspotCookie ? { hutk: hubspotCookie } : {}),
        ...(clientIP ? { ipAddress: clientIP } : {}),
      },
    };

    // Submit to HubSpot API
    const result = await submitToHubSpot(hubspotData);
    
    // Store contact email in sessionStorage for future tracking
    if (typeof window !== 'undefined' && formData.email) {
      sessionStorage.setItem('hubspot_contact_email', formData.email);
    }

    // Explicitly update contact to ensure correct firstname/lastname
    // This fixes issues where HubSpot form field mappings might cause incorrect values
    // IMPORTANT: Wait longer to ensure HubSpot has finished processing the form submission
    // and linked it to the contact before we update via CRM API
    if (result.success && formData.email) {
      // Wait longer for HubSpot to fully process form submission and link to contact
      // Increased delay to prevent "No contact record" issue in form submissions
      setTimeout(async () => {
        try {
          const updateResult = await updateHubSpotContact(
            formData.email, 
            formData.firstName.trim(), 
            formData.lastName.trim()
          );
          if (updateResult.success) {
            if (import.meta.env.DEV) {
              console.log('✅ Contact name explicitly updated in HubSpot:', { 
                firstName: formData.firstName, 
                lastName: formData.lastName 
              });
            }
          } else {
            console.warn('⚠️ Failed to explicitly update contact name:', updateResult.error);
            // Don't block form success - this is a correction step
          }
        } catch (error) {
          console.error('Error updating contact name:', error);
          // Don't block form success
        }
      }, 3000); // Increased to 3 seconds to ensure form submission is fully processed
    }
    
    // Link anonymous tracking to contact email (don't wait - fire and forget)
    if (formData.email) {
      linkAnonymousTracking(formData.email, formData.phone).catch(err => {
        console.error('Error linking anonymous tracking:', err);
      });
    }
    
    // If unitNumber is provided, handle unit-specific tracking and deal creation
    if (unitNumber && result.success) {
      // Process unit interest and deal creation asynchronously (don't block form success)
      (async () => {
        try {
          if (import.meta.env.DEV) {
            console.log('🔄 Starting async deal creation for unit:', unitNumber);
          }

          // Get unit data
          const { data: unit } = await getUnitByNumber(unitNumber);
          
          if (import.meta.env.DEV) {
            console.log('📦 Unit lookup result:', { unit: unit ? unit.unit_number : null, error: !unit });
          }
          
          if (unit && formData.email) {
            if (import.meta.env.DEV) {
              console.log('✅ Unit and email present, proceeding with deal creation');
            }
            // Mark form as submitted
            await markFormSubmitted(unit.id, formData.email);
            
            // Get unit interest record to calculate engagement score
            const interestResult = await getUnitInterest(unit.id, formData.email);
            
            if (import.meta.env.DEV) {
              console.log('📊 Unit interest result:', { 
                hasRecord: !!interestResult.record, 
                viewCount: interestResult.record?.view_count,
                clickCount: interestResult.record?.click_count 
              });
            }
            
            // Calculate engagement score and lead quality (use defaults if no record)
            let score = 0;
            let leadQuality: 'Hot' | 'Warm' | 'Cold' = 'Cold';
            
            if (interestResult.record) {
              // Calculate score from existing interest record
              score = calculateScoreFromRecord({
                view_count: interestResult.record.view_count,
                click_count: interestResult.record.click_count,
                form_submitted: true,
                meeting_scheduled: interestResult.record.meeting_scheduled,
                unit: { price: unit.price },
              });
              
              // Update engagement score in database
              await updateEngagementScore(unit.id, formData.email, score);
              
              // Determine lead quality from score
              leadQuality = getLeadQualityFromScore(score);
              
              if (import.meta.env.DEV) {
                console.log('📊 Engagement score calculated:', { score, leadQuality });
              }
            } else {
              if (import.meta.env.DEV) {
                console.log('📊 No prior interest record - using default values:', { score: 0, leadQuality: 'Cold' });
              }
            }
            
            // ALWAYS attempt to create deal (not conditional on interest record)
            if (import.meta.env.DEV) {
              console.log('⏳ Waiting 2 seconds before retrieving contact ID...');
            }
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            if (import.meta.env.DEV) {
              console.log('🔍 Attempting to retrieve contact ID for:', formData.email);
            }
            const contactId = await getContactIdByEmail(formData.email, 3, 2000);
            
            // Create HubSpot Deal if we have contact ID
            if (contactId) {
              if (import.meta.env.DEV) {
                console.log('📧 Contact ID retrieved:', contactId);
              }
              
              const leadSourceData = getLeadSourceFields().reduce((acc, field) => {
                acc[field.name] = field.value;
                return acc;
              }, {} as Record<string, string>);
              
              const dealResult = await createHubSpotDeal({
                unitId: unit.id,
                contactId: contactId,
                leadSource: leadSourceData,
                engagementScore: score,
                leadQuality: leadQuality,
              });
              
              if (dealResult.success) {
                console.log('✅ HubSpot Deal created:', dealResult.dealId);
              } else {
                console.warn('❌ Failed to create HubSpot Deal:', dealResult.error);
              }
            } else {
              console.warn('❌ Could not retrieve contact ID from HubSpot - deal not created');
            }
          } else {
            if (import.meta.env.DEV) {
              console.warn('⚠️ Skipping deal creation:', { 
                hasUnit: !!unit, 
                hasEmail: !!formData.email,
                unitNumber,
                email: formData.email 
              });
            }
          }
        } catch (error) {
          console.error('Error processing unit interest and deal creation:', error);
          // Don't block form success - these are secondary operations
        }
      })();
    }
    
    setLoading(false);
    
    if (result.success) {
      setSuccess(true);
      trackEvent('residences_unit_interest_registered', { 
        source: 'modal',
        ...(unitNumber ? { unit_number: unitNumber } : {})
      });
    } else {
      // Fallback: show success message even if HubSpot fails (graceful degradation)
      console.error('HubSpot submission error:', result.error);
      setSuccess(true);
      trackEvent('residences_unit_interest_registered', { 
        source: 'modal',
        ...(unitNumber ? { unit_number: unitNumber } : {})
      });
    }
    
    // Reset after showing success
    setTimeout(() => {
      setSuccess(false);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', interest: [], message: '', newsletter: false });
      onOpenChange(false);
    }, 3000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {success ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
            <DialogTitle className="text-2xl mb-2">Thank you!</DialogTitle>
            <DialogDescription className="text-base">
              We've received your enquiry and will be in touch within 24 hours.
            </DialogDescription>
            <p className="text-sm text-muted-foreground mt-4">
              Check your email for more information about The Pearl.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {unitNumber ? `Register Interest - Unit ${unitNumber}` : 'Register Your Interest'}
            </DialogTitle>
            <DialogDescription>
              {unitNumber 
                ? `Fill out the form below to register your interest in unit ${unitNumber}. We'll send you updates about pricing and availability.`
                : 'Fill out the form below and we\'ll send you updates about The Pearl, including pricing and availability.'}
            </DialogDescription>
            </DialogHeader>

            {/* Unit Info Display */}
            {unitNumber && (
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-muted/50 rounded-lg border border-border">
                {loadingUnit ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading unit details...</span>
                  </div>
                ) : unit ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="default" className="text-sm font-semibold">
                        Unit {unit.unit_number}
                      </Badge>
                      <Badge variant="outline">
                        {unit.category === '1bed' ? '1-Bedroom' : 
                         unit.category === '2bed' ? '2-Bedroom' : 
                         'Townhouse'} Residence
                      </Badge>
                      <Badge variant="outline">
                        {unit.view_type === 'lagoon' ? 'Lagoon View' : 'Canal View'}
                      </Badge>
                    </div>
                    {unit.price && unit.show_price && (
                      <div className="text-sm font-semibold text-foreground">
                        {formatCurrency(unit.price)}
                      </div>
                    )}
                    {unit.area_total && (
                      <div className="text-xs text-muted-foreground">
                        {unit.area_total.toLocaleString()} sq ft total area
                      </div>
                    )}
                    {/* Unit Location Map */}
                    <div className="mt-3 pt-3 border-t border-border/50">
                      <UnitLocationMap 
                        unitNumber={unit.unit_number} 
                        compact 
                        showTitle={false}
                        className="h-32"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    Unit {unitNumber}
                  </div>
                )}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 mt-4" noValidate>
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName || ''}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                  required
                  autoFocus
                />
              </div>
              
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName || ''}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Smith"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (345) 123-4567"
                  required
                />
              </div>
              
              <div>
                <Label>I'm interested in *</Label>
                <div className="space-y-1 sm:space-y-3 mt-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="interest-1bed"
                      checked={formData.interest.includes('1-Bedroom Residence')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, interest: [...formData.interest, '1-Bedroom Residence'] });
                        } else {
                          setFormData({ ...formData, interest: formData.interest.filter(i => i !== '1-Bedroom Residence') });
                        }
                      }}
                    />
                    <Label htmlFor="interest-1bed" className="cursor-pointer font-normal">
                      1-Bedroom Residence
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="interest-2bed"
                      checked={formData.interest.includes('2-Bedroom Residence')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, interest: [...formData.interest, '2-Bedroom Residence'] });
                        } else {
                          setFormData({ ...formData, interest: formData.interest.filter(i => i !== '2-Bedroom Residence') });
                        }
                      }}
                    />
                    <Label htmlFor="interest-2bed" className="cursor-pointer font-normal">
                      2-Bedroom Residence
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="interest-3bed"
                      checked={formData.interest.includes('3-Bedroom Residence')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, interest: [...formData.interest, '3-Bedroom Residence'] });
                        } else {
                          setFormData({ ...formData, interest: formData.interest.filter(i => i !== '3-Bedroom Residence') });
                        }
                      }}
                    />
                    <Label htmlFor="interest-3bed" className="cursor-pointer font-normal">
                      3-Bedroom Residence
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="interest-investment"
                      checked={formData.interest.includes('Investment Opportunity')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, interest: [...formData.interest, 'Investment Opportunity'] });
                        } else {
                          setFormData({ ...formData, interest: formData.interest.filter(i => i !== 'Investment Opportunity') });
                        }
                      }}
                    />
                    <Label htmlFor="interest-investment" className="cursor-pointer font-normal">
                      Investment Opportunity
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="interest-commercial"
                      checked={formData.interest.includes('Commercial Space')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, interest: [...formData.interest, 'Commercial Space'] });
                        } else {
                          setFormData({ ...formData, interest: formData.interest.filter(i => i !== 'Commercial Space') });
                        }
                      }}
                    />
                    <Label htmlFor="interest-commercial" className="cursor-pointer font-normal">
                      Commercial Space
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="interest-lagoon-facing"
                      checked={formData.interest.includes('Lagoon View')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, interest: [...formData.interest, 'Lagoon View'] });
                        } else {
                          setFormData({ ...formData, interest: formData.interest.filter(i => i !== 'Lagoon View') });
                        }
                      }}
                    />
                    <Label htmlFor="interest-lagoon-facing" className="cursor-pointer font-normal">
                      Lagoon View
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="interest-canal-facing"
                      checked={formData.interest.includes('Canal View')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, interest: [...formData.interest, 'Canal View'] });
                        } else {
                          setFormData({ ...formData, interest: formData.interest.filter(i => i !== 'Canal View') });
                        }
                      }}
                    />
                    <Label htmlFor="interest-canal-facing" className="cursor-pointer font-normal">
                      Canal View
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="interest-general"
                      checked={formData.interest.includes('General Information')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, interest: [...formData.interest, 'General Information'] });
                        } else {
                          setFormData({ ...formData, interest: formData.interest.filter(i => i !== 'General Information') });
                        }
                      }}
                    />
                    <Label htmlFor="interest-general" className="cursor-pointer font-normal">
                      General Information
                    </Label>
                  </div>
                </div>
                {formData.interest.length === 0 && (
                  <p className="text-sm text-destructive mt-1">Please select at least one interest</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="message">Message (optional)</Label>
                <Textarea
                  id="message"
                  value={formData.message || ''}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Any specific questions or requirements?"
                  rows={3}
                />
              </div>
              
              <div className="flex items-start space-x-2 min-h-[44px]">
                <Checkbox
                  id="newsletter"
                  checked={formData.newsletter}
                  onCheckedChange={(checked) => setFormData({ ...formData, newsletter: checked as boolean })}
                />
                <label htmlFor="newsletter" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                  Yes, I'd like to receive updates about The Pearl and other developments in Grand Cayman
                </label>
              </div>
              
              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Enquiry'
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground text-center">
                By submitting this form, you consent to be contacted by The Pearl team regarding your enquiry.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
